import { useCallback, useRef } from 'react';

export interface StateData<VAL, FIL> {
  nodeId: string;
  value: VAL | string;
  isTouched: boolean;
  filtered: FIL[];
}

export interface PersistentInputState<VAL, FIL> {
  data: StateData<VAL, FIL>;
  setNodeId: (v: string) => void;
  setValue: (v: VAL) => void;
  setIsTouched: (v: boolean) => void;
  setFiltered: (v: FIL[]) => void;
  reset: () => void;
}

/**
 * Keeps state  in a ref, persists between re-renders of Input
 */
export const usePersistentRenameInputState = <VAL, FIL>() => {
  // Map nodeId -> state
  const stateMap = useRef<StateData<VAL, FIL>>({
    nodeId: '',
    value: '',
    isTouched: false,
    filtered: []
  });

  // Setters update ref state
  const setNodeId = useCallback((v: string) => {
    stateMap.current.nodeId = v;
  }, []);
  const setValue = useCallback((v: VAL) => {
    stateMap.current.value = v;
  }, []);
  const setIsTouched = useCallback((v: boolean) => {
    stateMap.current.isTouched = v;
  }, []);
  const setFiltered = useCallback((v: FIL[]) => {
    stateMap.current.filtered = v;
  }, []);
  const reset = () => {
    stateMap.current = {
      nodeId: '',
      value: '',
      isTouched: false,
      filtered: []
    };
  };

  return {
    data: stateMap.current,
    setNodeId,
    setValue,
    setIsTouched,
    setFiltered,
    reset
  } satisfies PersistentInputState<VAL, FIL>;
};
